import { useEffect, useState } from "react";
import { Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, money } from "@/lib/api";
import { can } from "@/lib/rbac";
import { useAuth } from "@/context/AuthContext";
import { RolesPanel } from "@/components/RolesPanel";
import { FactoryReset, OperationalReset, ClearCollections, DedupeJobs } from "@/components/FactoryReset";
import { DemoEnvironment } from "@/components/DemoEnvironment";
import { BrandModels } from "@/components/BrandModels";
import { PriceLists } from "@/components/PriceLists";
import { ServiceMaterials } from "@/components/ServiceMaterials";
import { PageHead } from "@/components/Bits";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const CONTENT_FORMATS = ["Story", "Reel", "Reel or Story", "Reel or talking video", "Post or Story",
  "Reel or longer video", "Static", "Carousel", "YouTube short", "Google post"];
const CADENCES = ["daily", "weekly", "every 2 weeks", "fortnightly", "monthly", "on-demand", "off"];
const PURCHASE_UNITS = ["Roll", "Bottle", "Litre", "Piece", "Kit", "Set", "Tin", "sqft", "ml"];

const TABS = [
  ["brands", "Car brands & models"],
  ["price-lists", "PPF price lists"],
  ["pipeline-stages", "Pipeline stages"],
  ["b2b-stages", "B2B pipeline stages"],
  ["roles", "Roles & access"],
  ["scheduling", "Scheduling settings"],
  ["services", "Services catalog"],
  ["skus", "SKU catalog"],
  ["regions", "Regions"],
  ["lead-sources", "Lead sources"],
  ["demo", "Demo environment"],
  ["backdate", "Backdated entry"],
  ["danger", "Danger zone"],
];

export default function Configuration() {
  const { user, refreshUser } = useAuth();
  const isAdmin = can(user, "catalog:write");
  const isNetworkAdmin = user.role === "network_admin";
  const [tab, setTab] = useState("brands");
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [sched, setSched] = useState(null);
  const [svc, setSvc] = useState([]);

  useEffect(() => {
    api.get("/settings/scheduling").then(({ data }) => setSched(data));
  }, []);

  useEffect(() => {
    if (tab === "scheduling") api.get("/config/services").then(({ data }) => setSvc(data));
  }, [tab]);

  const saveScheduling = async () => {
    try {
      const { data } = await api.put("/settings/scheduling", {
        travel_buffer_mode: sched.travel_buffer_mode,
        travel_buffer_hours: Number(sched.travel_buffer_hours || 0),
      });
      setSched(data);
      toast.success("Scheduling settings updated network-wide");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const saveDuration = async (row) => {
    try {
      await api.put(`/config/services/${row.id}`, {
        name: row.name,
        default_labor_cost: row.default_labor_cost || 0,
        requires_before_after: !!row.requires_before_after,
        warranty_months: row.warranty_months || 0,
        warranty_type: row.warranty_type || "",
        duration_days: Math.max(1, Number(row.duration_days || 1)),
        active: row.active !== false,
      });
      toast.success(`${row.name} · ${Math.max(1, Number(row.duration_days || 1))} day(s)`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const toggleBackdate = async (active) => {
    try {
      await api.post("/admin/backdate-mode", { active });
      await refreshUser();
      toast.success(active ? "Backdated entry mode is ON" : "Backdated entry mode turned off");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const load = () => {
    if (tab === "scheduling" || tab === "roles" || tab === "danger" || tab === "backdate" || tab === "demo" || tab === "price-lists") return;
    if (tab === "skus") api.get("/catalog").then(({ data }) => setRows(data));
    else
      api
        .get(`/config/${tab}`, { params: { include_inactive: true } })
        .then(({ data }) => setRows(data));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const blank = () =>
    tab === "pipeline-stages" || tab === "b2b-stages"
      ? { name: "", order: rows.length, active: true }
      : tab === "content-types"
      ? { name: "", format: "Reel", default_cadence: "weekly", purpose: "", presenter_led: false, active: true }
      : tab === "skus"
      ? {
          name: "",
          category: "PPF Film",
          unit: "Roll",
          consumption_unit: "sqft",
          purchase_unit_size: "245",
          hq_cost_price: "",
          hq_sale_price: "",
        }
      : { name: "", models: "", default_labor_cost: "", requires_before_after: false, duration_days: 1, active: true };

  const save = async () => {
    try {
      if (tab === "content-types") {
        const payload = {
          name: draft.name,
          format: draft.format || "Reel",
          default_cadence: draft.default_cadence || "weekly",
          purpose: draft.purpose || "",
          presenter_led: !!draft.presenter_led,
          active: draft.active !== false,
        };
        if (draft.id) await api.put(`/config/${tab}/${draft.id}`, payload);
        else await api.post(`/config/${tab}`, payload);
        toast.success("Saved");
        setDraft(null);
        load();
        return;
      }
      if (tab === "skus") {
        const payload = {
          name: draft.name,
          category: draft.category,
          unit: (draft.unit || "").trim(),
          consumption_unit: (draft.consumption_unit || "").trim(),
          purchase_unit_size: Math.max(Number(draft.purchase_unit_size || 1), 0.0001),
          hq_cost_price: Number(draft.hq_cost_price || 0),
          hq_sale_price: Number(draft.hq_sale_price || 0),
        };
        if (draft.id) await api.put(`/catalog/${draft.id}`, payload);
        else await api.post("/catalog", payload);
      } else {
        const payload = {
          name: draft.name,
          order: Number(draft.order || 0),
          models: String(draft.models || "").split(",").map((m) => m.trim()).filter(Boolean),
          default_labor_cost: Number(draft.default_labor_cost || 0),
          requires_before_after: !!draft.requires_before_after,
          warranty_months: Number(draft.warranty_months || 0),
          warranty_type: draft.warranty_type || "",
          duration_days: Math.max(1, Number(draft.duration_days || 1)),
          active: draft.active !== false,
        };
        if (draft.id) await api.put(`/config/${tab}/${draft.id}`, payload);
        else await api.post(`/config/${tab}`, payload);
      }
      toast.success("Configuration saved");
      setDraft(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const deleteItem = async () => {
    try {
      await api.delete(
        tab === "skus" ? `/catalog/${toDelete.id}` : `/config/${tab}/${toDelete.id}`
      );
      toast.success(`${toDelete.name} deleted`);
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const toggleActive = async (row) => {
    try {
      await api.put(`/config/${tab}/${row.id}`, {
        name: row.name,
        order: row.order || 0,
        models: row.models || [],
        default_labor_cost: row.default_labor_cost || 0,
        requires_before_after: !!row.requires_before_after,
        warranty_months: row.warranty_months || 0,
        warranty_type: row.warranty_type || "",
        duration_days: Math.max(1, Number(row.duration_days || 1)),
        format: row.format || "",
        default_cadence: row.default_cadence || "weekly",
        purpose: row.purpose || "",
        presenter_led: !!row.presenter_led,
        active: !row.active,
      });
      toast.success(row.active ? "Deactivated" : "Reactivated");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <PageHead title="Configuration" subtitle="Master data used by dropdowns across the platform">
        {tab !== "scheduling" && tab !== "roles" && tab !== "brands" && tab !== "price-lists" && tab !== "danger" && tab !== "backdate" && tab !== "demo" && (
          <Button data-testid="config-add-btn" onClick={() => setDraft(blank())}>
            <Plus className="mr-2 h-4 w-4" /> Add
          </Button>
        )}
      </PageHead>

      <div className="mb-4 flex flex-wrap gap-2">
        {TABS.filter(([k]) =>
          k === "danger" ? isAdmin : k === "backdate" || k === "demo" ? isNetworkAdmin : true
        ).map(([k, label]) => (
          <button
            key={k}
            data-testid={`config-tab-${k}`}
            onClick={() => {
              setTab(k);
              setDraft(null);
            }}
            className={`rounded px-3 py-1.5 text-sm font-medium transition-colors duration-200 ${
              tab === k ? "bg-brand text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <Dialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <DialogContent className="sm:max-w-md" data-testid="sku-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete {toDelete?.name}?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            {tab === "skus"
              ? `This permanently removes the SKU from the material catalog. If it is linked to any job, order, studio stock record or consumption template the delete is blocked and you should deactivate it instead.`
              : `This permanently removes the service from the service catalogue. If it is used by any job, quote or monthly target the delete is blocked and you should deactivate it instead.`}
          </p>
          <DialogFooter>
            <Button variant="outline" className="min-h-[44px]" onClick={() => setToDelete(null)}>
              Cancel
            </Button>
            <Button
              className="min-h-[44px] bg-rose-600 text-white hover:bg-rose-700"
              data-testid="confirm-delete-sku-btn"
              onClick={deleteItem}
            >
              Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {tab === "roles" && <RolesPanel />}

      {tab === "backdate" && isNetworkAdmin && (
        <Card className="border-amber-300 shadow-none" data-testid="backdate-settings-card">
          <CardHeader>
            <CardTitle className="text-base">Backdated entry mode</CardTitle>
            <p className="mt-1 text-xs text-slate-500">
              Admin-only. While ON, the create forms for jobs, warranty registrations, protection
              plans and stock purchases/orders show a "Historical entry date" field so you can log
              past records with their real date. Those records are stored on that date and stay out
              of the current month's figures. Turn it off when you're done.
            </p>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-4">
            <Badge
              data-testid="backdate-state-badge"
              className={`border-0 ${
                user.backdate_mode ? "bg-amber-100 text-amber-900" : "bg-slate-100 text-slate-500"
              }`}
            >
              {user.backdate_mode ? "ON — recording historical data" : "Off — using today's date"}
            </Badge>
            {user.backdate_mode ? (
              <Button
                className="min-h-[44px] bg-slate-700 text-white hover:bg-slate-800"
                data-testid="backdate-turn-off-btn"
                onClick={() => toggleBackdate(false)}
              >
                Turn off — resume today's date
              </Button>
            ) : (
              <Button
                className="min-h-[44px] bg-amber-500 text-white hover:bg-amber-600"
                data-testid="backdate-turn-on-btn"
                onClick={() => toggleBackdate(true)}
              >
                Turn on backdated entry
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {tab === "danger" && isAdmin && (
        <div className="space-y-6">
          <DedupeJobs />
          <ClearCollections />
          <OperationalReset />
          <FactoryReset />
        </div>
      )}

      {tab === "demo" && isNetworkAdmin && <DemoEnvironment />}

      {tab === "scheduling" && sched && (
        <div className="space-y-6">
          <Card className="border-brand-line shadow-none" data-testid="service-durations-card">
            <CardHeader>
              <CardTitle className="text-base">Estimated duration per service</CardTitle>
              <p className="mt-1 text-xs text-slate-500">
                A job blocks its installer team for the full duration of its longest service.
              </p>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {svc.map((s, i) => (
                <div key={s.id} className="flex items-end gap-2">
                  <div className="flex-1">
                    <Label className="text-xs">{s.name}</Label>
                    <Input
                      className="mt-1.5 min-h-[44px]"
                      inputMode="numeric"
                      data-testid={`service-duration-${s.id}`}
                      value={s.duration_days ?? 1}
                      onChange={(e) =>
                        setSvc((p) =>
                          p.map((r, idx) => (idx === i ? { ...r, duration_days: e.target.value } : r))
                        )
                      }
                    />
                  </div>
                  <Button
                    variant="outline"
                    className="min-h-[44px]"
                    data-testid={`save-service-duration-${s.id}`}
                    onClick={() => saveDuration(s)}
                  >
                    <Save className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <p className="text-xs text-slate-500 sm:col-span-2 lg:col-span-3">
                Duration is in days. Editing a service here is the same as editing it in the
                services catalog.
              </p>
            </CardContent>
          </Card>

          <Card className="border-brand-line shadow-none" data-testid="scheduling-settings-card">
            <CardHeader>
              <CardTitle className="text-base">Travel buffer</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap items-end gap-6">
              <div>
                <Label>Travel buffer mode</Label>
                <div className="mt-1.5 flex gap-2">
                  {[
                    ["distance", "Distance-based"],
                    ["fixed", "Fixed"],
                  ].map(([k, label]) => (
                    <button
                      key={k}
                      data-testid={`buffer-mode-${k}`}
                      onClick={() => setSched((p) => ({ ...p, travel_buffer_mode: k }))}
                      className={`min-h-[44px] rounded px-4 text-sm font-medium transition-colors duration-200 ${
                        sched.travel_buffer_mode === k
                          ? "bg-brand text-white"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {sched.travel_buffer_mode === "fixed" && (
                <div>
                  <Label>Flat travel buffer (hours)</Label>
                  <Input
                    className="mt-1.5 w-56 min-h-[44px]"
                    data-testid="sched-travel_buffer_hours-input"
                    value={sched.travel_buffer_hours ?? 0}
                    onChange={(e) =>
                      setSched((p) => ({ ...p, travel_buffer_hours: e.target.value }))
                    }
                  />
                </div>
              )}
              <Button
                className="min-h-[44px]"
                data-testid="save-scheduling-settings-btn"
                onClick={saveScheduling}
              >
                <Save className="mr-2 h-4 w-4" /> Save settings
              </Button>
              <p className="w-full text-xs text-slate-500" data-testid="buffer-mode-hint">
                {sched.travel_buffer_mode === "fixed"
                  ? "A flat buffer is applied to every job automatically."
                  : "Travel time is entered manually for each job when the slot is booked."}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {tab === "brands" && <BrandModels canWrite={isAdmin} />}
      {tab === "price-lists" && <PriceLists canWrite={isAdmin} />}

      {tab === "services" && <ServiceMaterials canWrite={isAdmin} />}

      {draft && tab !== "brands" && tab !== "danger" && (
        <Card className="mb-4 border-brand-line shadow-none">
          <CardHeader>
            <CardTitle className="text-base">{draft.id ? "Edit" : "New"} entry</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-end gap-3">
            <div>
              <Label>Name</Label>
              <Input
                className="mt-1.5 w-56"
                data-testid="config-name-input"
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              />
            </div>
            {tab === "brands" && (
              <div>
                <Label>Models (comma separated)</Label>
                <Input
                  className="mt-1.5 w-72"
                  data-testid="config-models-input"
                  value={draft.models}
                  onChange={(e) => setDraft((d) => ({ ...d, models: e.target.value }))}
                />
              </div>
            )}
            {(tab === "pipeline-stages" || tab === "b2b-stages") && (
              <div>
                <Label>Order</Label>
                <Input
                  className="mt-1.5 w-24"
                  data-testid="config-order-input"
                  value={draft.order ?? ""}
                  onChange={(e) => setDraft((d) => ({ ...d, order: e.target.value }))}
                />
              </div>
            )}
            {tab === "services" && (
              <>
                <div>
                  <Label>Default labor cost (₹)</Label>
                  <Input
                    className="mt-1.5 w-40"
                    data-testid="config-labor-input"
                    value={draft.default_labor_cost}
                    onChange={(e) => setDraft((d) => ({ ...d, default_labor_cost: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Estimated duration (days)</Label>
                  <Input
                    className="mt-1.5 w-40"
                    inputMode="numeric"
                    data-testid="config-duration-input"
                    value={draft.duration_days ?? 1}
                    onChange={(e) => setDraft((d) => ({ ...d, duration_days: e.target.value }))}
                  />
                </div>
                <label className="flex items-center gap-2 pb-2 text-sm">
                  <input
                    type="checkbox"
                    data-testid="config-before-after-checkbox"
                    checked={!!draft.requires_before_after}
                    onChange={(e) => setDraft((d) => ({ ...d, requires_before_after: e.target.checked }))}
                  />
                  Requires before/after content
                </label>
              </>
            )}
            {tab === "content-types" && (
              <>
                <div>
                  <Label>Format</Label>
                  <Select
                    value={draft.format || "Reel"}
                    onValueChange={(v) => setDraft((d) => ({ ...d, format: v }))}
                  >
                    <SelectTrigger className="mt-1.5 w-44" data-testid="config-content-format-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTENT_FORMATS.map((f) => (
                        <SelectItem key={f} value={f}>
                          {f}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Default cadence</Label>
                  <Select
                    value={draft.default_cadence || "weekly"}
                    onValueChange={(v) => setDraft((d) => ({ ...d, default_cadence: v }))}
                  >
                    <SelectTrigger className="mt-1.5 w-40" data-testid="config-content-cadence-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CADENCES.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="min-w-[18rem] flex-1">
                  <Label>Purpose</Label>
                  <Input
                    className="mt-1.5"
                    placeholder="Why this content type exists — what it should achieve"
                    data-testid="config-content-purpose-input"
                    value={draft.purpose ?? ""}
                    onChange={(e) => setDraft((d) => ({ ...d, purpose: e.target.value }))}
                  />
                </div>
                <label className="flex items-center gap-2 pb-2 text-sm">
                  <input
                    type="checkbox"
                    data-testid="config-presenter-led-checkbox"
                    checked={!!draft.presenter_led}
                    onChange={(e) => setDraft((d) => ({ ...d, presenter_led: e.target.checked }))}
                  />
                  Presenter-led (someone on camera)
                </label>
              </>
            )}
            {tab === "skus" && (
              <>
                <div>
                  <Label>Category</Label>
                  <Input
                    className="mt-1.5 w-36"
                    data-testid="config-sku-category-input"
                    value={draft.category ?? ""}
                    onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Purchase unit</Label>
                  <Select
                    value={PURCHASE_UNITS.includes(draft.unit) ? draft.unit : "Custom"}
                    onValueChange={(v) =>
                      setDraft((d) => ({ ...d, unit: v === "Custom" ? "" : v }))
                    }
                  >
                    <SelectTrigger className="mt-1.5 w-40" data-testid="config-sku-purchase-unit-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...PURCHASE_UNITS, "Custom"].map((u) => (
                        <SelectItem key={u} value={u}>
                          {u}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {!PURCHASE_UNITS.includes(draft.unit) && (
                  <div>
                    <Label>Custom unit name</Label>
                    <Input
                      className="mt-1.5 w-36"
                      placeholder="Kit, Set, Tin…"
                      data-testid="config-sku-custom-unit-input"
                      value={draft.unit ?? ""}
                      onChange={(e) => setDraft((d) => ({ ...d, unit: e.target.value }))}
                    />
                  </div>
                )}
                <div>
                  <Label>Consumption unit</Label>
                  <Input
                    className="mt-1.5 w-32"
                    placeholder="sqft, ml…"
                    data-testid="config-sku-consumption-unit-input"
                    value={draft.consumption_unit ?? ""}
                    onChange={(e) => setDraft((d) => ({ ...d, consumption_unit: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Purchase unit size</Label>
                  <Input
                    className="mt-1.5 w-32"
                    inputMode="decimal"
                    data-testid="config-sku-unit-size-input"
                    value={draft.purchase_unit_size ?? ""}
                    onChange={(e) => setDraft((d) => ({ ...d, purchase_unit_size: e.target.value }))}
                  />
                </div>
                <p className="w-full text-xs text-slate-500" data-testid="config-sku-unit-hint">
                  1 {draft.unit || "unit"} = {draft.purchase_unit_size || 1}{" "}
                  {draft.consumption_unit || "unit"}. Stock is held in{" "}
                  {draft.unit || "purchase unit"}s and consumption is entered in{" "}
                  {draft.consumption_unit || "the consumption unit"}. Prices below are per{" "}
                  {draft.unit || "purchase unit"}.
                </p>
                {isAdmin && (
                  <div>
                    <Label>HQ cost price</Label>
                    <Input
                      className="mt-1.5 w-36"
                      data-testid="config-sku-hq_cost_price-input"
                      value={draft.hq_cost_price ?? ""}
                      onChange={(e) => setDraft((d) => ({ ...d, hq_cost_price: e.target.value }))}
                    />
                  </div>
                )}
                <div>
                  <Label>HQ sale price</Label>
                  <Input
                    className="mt-1.5 w-36"
                    data-testid="config-sku-hq_sale_price-input"
                    value={draft.hq_sale_price ?? ""}
                    onChange={(e) => setDraft((d) => ({ ...d, hq_sale_price: e.target.value }))}
                  />
                </div>
              </>
            )}
            <Button data-testid="config-save-btn" onClick={save} disabled={!draft.name}>
              <Save className="mr-2 h-4 w-4" /> Save
            </Button>
            <Button variant="ghost" onClick={() => setDraft(null)}>
              Cancel
            </Button>
          </CardContent>
        </Card>
      )}

      {tab !== "scheduling" && tab !== "roles" && tab !== "brands" && tab !== "price-lists" && tab !== "danger" && tab !== "backdate" && tab !== "demo" && (
      <Card className="border-brand-line shadow-none">
        <CardContent className="overflow-x-auto p-0">
          <Table data-testid="config-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Details</TableHead>
                <TableHead>Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} className="hover:bg-brand-tint/60">
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell className="max-w-md text-slate-500">
                    {tab === "brands" && (r.models || []).join(", ")}
                    {(tab === "pipeline-stages" || tab === "b2b-stages") && `Position ${r.order ?? 0}`}
                    {tab === "content-types" && (
                      <>
                        {`${r.format || "—"} · default ${r.default_cadence || "weekly"}`}
                        {r.presenter_led ? " · presenter-led" : ""}
                        {r.purpose && (
                          <span className="block text-xs text-slate-400">{r.purpose}</span>
                        )}
                      </>
                    )}
                    {tab === "services" &&
                      `Labor ${money(r.default_labor_cost)} · ${r.duration_days ?? 1} day(s)${r.requires_before_after ? " · before/after required" : ""}`}
                    {tab === "skus" && (
                      <>
                        {isAdmin
                          ? `${r.category} · cost ${money(r.hq_cost_price)} · sale ${money(r.hq_sale_price)} per ${r.unit}`
                          : `${r.category} · sale ${money(r.hq_sale_price)} per ${r.unit}`}
                        <span className="block text-xs text-slate-400" data-testid={`sku-unit-${r.id}`}>
                          1 {r.unit} = {r.purchase_unit_size ?? 1} {r.consumption_unit || r.unit}
                        </span>
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge
                      data-testid={`config-status-${r.id}`}
                      className={`border-0 ${
                        r.active === false ? "bg-slate-100 text-slate-500" : "bg-emerald-50 text-emerald-700"
                      }`}
                    >
                      {r.active === false ? "Inactive" : "Active"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid={`config-edit-${r.id}`}
                      onClick={() =>
                        setDraft({ ...r, models: (r.models || []).join(", ") })
                      }
                    >
                      Edit
                    </Button>
                    {tab !== "skus" && (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`config-toggle-${r.id}`}
                        onClick={() => toggleActive(r)}
                      >
                        {r.active === false ? "Reactivate" : "Deactivate"}
                      </Button>
                    )}
                    {(tab === "skus" || tab === "services") && isAdmin && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="ml-2 border-rose-200 text-rose-600 hover:bg-rose-50"
                        data-testid={`config-delete-${r.id}`}
                        onClick={() => setToDelete(r)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      )}
    </div>
  );
}
